import { Grid } from '@mui/material';
import { useSearchQueryParam } from '@piaas/piaas-ui-toolkit/hooks';
import { ODSCardBasic, ODSDivider } from '@telekom-ods/react-ui-kit';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import Provision from '../../../../../api/provision';
import Alert from '../../../../../components/Alert';
import BreadCrumb from '../../../../../components/BreadCrumb';
import { ScrollToTop } from '../../../../../components/ScrollToTop/ScrollToTop';
import Table from '../../../../../components/Table';
import { SELECTED_CLUSTER_PARAM } from '../../../../../helpers/searchParams';
import useApiQuery from '../../../../../hooks/useApiQuery';
import useTableApiCall from '../../../../../hooks/useTableApiCall';
import ReportStyle from '../../../../Reports/Reports.module.css';

import CaasClusterDetails from './CaasClusterDetails';

import type { CaasCluster } from '@/types/productTypes';
import type { TableRowData } from '@/types/tableTypes';

const rowClusterName = (row: TableRowData): string | undefined =>
  row.data.find(cell => cell.field === 'name')?.value as string | undefined;

const fetchClusterDetails: Parameters<
  ReturnType<typeof useApiQuery<CaasCluster>>['makeApiCall']
>[0] extends never
  ? never
  : Parameters<typeof useApiQuery<CaasCluster>>[0] = async clusterName => {
  if (typeof clusterName !== 'string') {
    throw new Error('Cluster name must be a string.');
  }

  const response = await Provision.fetchClusterDetailsV1(clusterName);

  return response.data as CaasCluster;
};

const fetchCaasClusters: Parameters<typeof useTableApiCall>[0] = params => {
  return Provision.fetchCaasClustersV1(params);
};

const CaasClusters = (): React.JSX.Element => {
  const intl = useIntl();
  const [rows, setRows] = useState<TableRowData[]>([]);

  const { makeApiCall: getCaasClusterDetails, error: clusterDetailsError } =
    useApiQuery<CaasCluster>(fetchClusterDetails);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClusterName = searchParams.get(SELECTED_CLUSTER_PARAM) ?? '';

  const selectCluster = useCallback(
    (name: string) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        next.set(SELECTED_CLUSTER_PARAM, name);
        return next;
      });
    },
    [setSearchParams]
  );

  const [clusterDetails, setClusterDetails] = useState<CaasCluster | null>(null);

  const getCaaSClusterDetails = useCallback(
    async (clusterName: string) => {
      try {
        const data = await getCaasClusterDetails(clusterName);
        if (data) {
          setClusterDetails(data);
        }
      } catch {
        setClusterDetails(null);
      }
    },
    [getCaasClusterDetails]
  );

  const transformClustersToRows = useCallback(
    (clusters: CaasCluster[]): TableRowData[] =>
      clusters.map((cluster: CaasCluster) => {
        const clusterKey = `${cluster.name}-${cluster.cloud_id}-${cluster.type}`;

        return {
          key: clusterKey,
          onRowClick: () => {
            selectCluster(cluster.name);
          },
          data: [
            { field: 'name', value: cluster.name },
            { field: 'type', value: cluster.type },
            { field: 'version', value: cluster.version },
            { field: 'cloud_id', value: cluster.cloud_id },
            { field: 'created', value: cluster.created },
            { field: 'last_updated', value: cluster.last_updated },
            { field: 'device_count', value: cluster.device_count },
            { field: 'juice_group', value: cluster.juice_group },
            { field: 'owner', value: typeof cluster.owner === 'string' ? cluster.owner : '' },
          ],
        };
      }),
    [selectCluster]
  );

  const fetchDetailsRef = useRef(getCaaSClusterDetails);
  fetchDetailsRef.current = getCaaSClusterDetails;

  useEffect(() => {
    if (!selectedClusterName) {
      setClusterDetails(null);
      return;
    }

    void fetchDetailsRef.current(selectedClusterName);
  }, [selectedClusterName]);

  const selectedClusterKey = useMemo(
    () =>
      selectedClusterName
        ? (rows.find(row => rowClusterName(row) === selectedClusterName)?.key ?? '')
        : '',
    [rows, selectedClusterName]
  );

  const handleRowsUpdate = useCallback(
    (clusters: CaasCluster[]) => {
      setRows(transformClustersToRows(clusters));
    },
    [transformClustersToRows]
  );

  const setTableRows = useCallback<NonNullable<Parameters<typeof useTableApiCall>[1]['setRows']>>(
    clusters => {
      handleRowsUpdate(clusters as CaasCluster[]);
    },
    [handleRowsUpdate]
  );

  const [initialSearch] = useSearchQueryParam('caasClustersSearch');

  const { setCurrentPage, itemsPerPage, setItemsPerPage, ...tableData } = useTableApiCall(
    fetchCaasClusters,
    {
      isPaginated: true,
      setRows: setTableRows,
      searchColumns: ['name'],
      initialFilter: initialSearch,
      searchParamKey: 'caasClustersSearch',
    }
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  // Stable identity - a fresh one each render would reset Search.tsx's debounce.
  const setTableFilter = useCallback<NonNullable<React.ComponentProps<typeof Table>['setFilter']>>(
    filter => {
      setCurrentPage(0);
      tableData.setFilter(typeof filter === 'string' ? filter : '');
    },
    // `tableData` is a fresh object every render; its `setFilter` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setCurrentPage, tableData.setFilter]
  );

  const fields = [
    { id: 'name', title: { id: 'name' }, isAdvancedSearchable: true },
    { id: 'type', title: { id: 'type' }, isAdvancedSearchable: true },
    { id: 'version', title: { id: 'version' } },
    { id: 'cloud_id', title: { id: 'cloud' }, isAdvancedSearchable: true },
    { id: 'created', title: { id: 'created' } },
    { id: 'last_updated', title: { id: 'lastUpdated' } },
    { id: 'device_count', title: { id: 'deviceCount' } },
    { id: 'juice_group', title: { id: 'juiceGroup' }, isAdvancedSearchable: true },
    { id: 'owner', title: { id: 'owner' } },
  ];

  const showCaasClusterDetails = Boolean(
    selectedClusterName && clusterDetails && Object.keys(clusterDetails).length > 0
  );

  const scrolledForRef = useRef('');

  useEffect(() => {
    if (!showCaasClusterDetails || rows.length === 0) {
      if (!selectedClusterName) scrolledForRef.current = '';
      return;
    }

    if (scrolledForRef.current === selectedClusterName) return;

    scrolledForRef.current = selectedClusterName;
    document.getElementById('caas-details')?.scrollIntoView({ behavior: 'smooth' });
  }, [showCaasClusterDetails, rows.length, selectedClusterName]);

  const detailsAlertTitle = clusterDetailsError?.message
    ? intl.formatMessage(
        { id: 'failedToLoadCaasClusterDetails' },
        { message: clusterDetailsError.message }
      )
    : intl.formatMessage({ id: 'noCaasClusterSelected' });

  return (
    <Grid
      container
      direction='column'
      gap={2}
    >
      <Grid item>
        <BreadCrumb
          items={[
            {
              href: '/',
              text: intl.formatMessage({ id: 'title' }),
            },
            {
              href: '#',
              text: intl.formatMessage({ id: 'caasCluster' }),
            },
          ]}
        />
      </Grid>
      <Grid item>
        <Table
          header={intl.formatMessage({ id: 'clusters' })}
          fields={fields}
          rows={rows}
          pagination
          {...tableData}
          pageSize={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          setFilter={setTableFilter}
          onPageChange={handlePageChange}
          error={tableData.error ? tableData.error.message : null}
          advancedSearch={true}
          searchParamKey='caasClustersSearch'
          allowSelectedHighlight={{
            selectedKey: selectedClusterKey,
          }}
        />
      </Grid>
      <ODSDivider
        className='divider'
        variant='horizontal'
      />
      <Grid item>
        <ODSCardBasic
          className={`${ReportStyle.tenant_details_card ?? ''} ${
            showCaasClusterDetails
              ? (ReportStyle.hypervisor_details_card ?? '')
              : (ReportStyle.tenant_details_card_without_spacing ?? '')
          }`}
          contentSlot={
            <div id='caas-details'>
              {showCaasClusterDetails ? (
                <>
                  <div
                    style={{
                      width: '99%',
                    }}
                  >
                    <CaasClusterDetails
                      {...(clusterDetails && {
                        clusterData: clusterDetails,
                      })}
                    />
                  </div>

                  <ScrollToTop
                    className={`${ReportStyle.scroll_top ?? ''} ${ReportStyle.hypervisor_scroll_top ?? ''}`}
                    fixed={false}
                    scrollContainerClass='page-content-scrollable'
                  />
                </>
              ) : (
                <Alert
                  className={ReportStyle.select_tenant_alert ?? ''}
                  mode='error'
                  title={detailsAlertTitle}
                />
              )}
            </div>
          }
          size='large'
          tabIndex={-1}
        />
      </Grid>
    </Grid>
  );
};

export default CaasClusters;
